import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, AlertCircle, XCircle, CheckCircle, Clock } from 'lucide-react';
import './AlertsTab.css';

const AlertsTab = ({ hotelId, token }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const alertTypes = [
    { value: '', label: 'All Alerts' },
    { value: 'low_stock', label: 'Low Stock', icon: AlertCircle, class: 'alert-warning' },
    { value: 'zero_stock', label: 'Out of Stock', icon: XCircle, class: 'alert-danger' },
    { value: 'expiring_soon', label: 'Expiring Soon', icon: Clock, class: 'alert-info' },
    { value: 'expired', label: 'Expired', icon: AlertTriangle, class: 'alert-danger' }
  ];

  useEffect(() => {
    if (hotelId && token) {
      fetchAlerts();
    }
  }, [hotelId, token, filterType, filterStatus]);

  const fetchAlerts = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (hotelId) queryParams.set('hotel_id', String(hotelId));
      if (filterType) queryParams.set('alert_type', filterType);
      if (filterStatus) {
        queryParams.set('unresolved_only', filterStatus === 'pending' ? 'true' : 'false');
      }

      const url = `${process.env.REACT_APP_API_URL}/api/inventory/reports/alerts?${queryParams.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const rows = Array.isArray(response.data?.data?.alerts)
        ? response.data.data.alerts
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

      setAlerts(rows);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts');
      setLoading(false);
    }
  };

  const handleResolve = async (alert, resolutionNotes = '', shouldCreatePo = false) => {

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/inventory/reports/alerts/${alert.alert_id}/resolve`,
        {
          hotel_id: hotelId,
          resolution_notes: resolutionNotes || 'Resolved via dashboard',
          create_purchase_order: shouldCreatePo
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(shouldCreatePo ? 'Purchase order created and alert resolved successfully' : 'Alert resolved successfully');
      fetchAlerts();
    } catch (err) {
      console.error('Error resolving alert:', err);
      setError(err.response?.data?.message || 'Failed to resolve alert');
    }
  };

  const getAlertIcon = (type) => {
    const config = alertTypes.find(t => t.value === type);
    if (!config) return <AlertCircle size={20} />;
    const Icon = config.icon;
    return <Icon size={20} className={config.class} />;
  };

  const getAlertPriority = (type) => {
    if (type === 'zero_stock' || type === 'expired') return 'high';
    if (type === 'low_stock' || type === 'expiring_soon') return 'medium';
    return 'low';
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading alerts...</p>
      </div>
    );
  }

  const safeAlerts = Array.isArray(alerts) ? alerts : [];
  const pendingAlerts = safeAlerts.filter((a) => !a.is_resolved).length;
  const resolvedAlerts = safeAlerts.filter((a) => a.is_resolved).length;
  const highPriorityAlerts = safeAlerts.filter((a) => getAlertPriority(a.alert_type) === 'high' && !a.is_resolved).length;

  return (
    <div className="alerts-tab">
      <div className="tab-header">
        <h2><AlertTriangle size={24} /> Inventory Alerts</h2>
        <div className="alert-counts">
          <span className="count-badge count-danger">{pendingAlerts} Pending</span>
          <span className="count-badge count-success">{resolvedAlerts} Resolved</span>
          <span className="count-badge count-warning">{highPriorityAlerts} High Priority</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="filters-bar">
        <div className="filter-group">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            {alertTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div className="alerts-list">
        {safeAlerts.map((alert) => {
          const priority = getAlertPriority(alert.alert_type);
          const isResolved = Boolean(alert.is_resolved);

          return (
            <div 
              key={alert.alert_id} 
              className={`alert-item priority-${priority} ${isResolved ? 'resolved' : ''}`}
            >
              <div className="alert-icon">
                {getAlertIcon(alert.alert_type)}
              </div>

              <div className="alert-content">
                <div className="alert-header">
                  <h4>{alert.item_name}</h4>
                  <span className={`priority-badge priority-${priority}`}>
                    {priority.toUpperCase()}
                  </span>
                </div>

                <p className="alert-message">{alert.message || alert.alert_message}</p>

                <div className="alert-details">
                  <span className="alert-type">{alert.alert_type.replace(/_/g, ' ')}</span>
                  <span className="alert-date">
                    Created: {new Date(alert.created_at).toLocaleString()}
                  </span>
                  {alert.item_id && (
                    <span className="alert-item-info">
                      Item ID: {alert.item_id}
                    </span>
                  )}
                </div>

                {isResolved && (
                  <div className="resolution-info">
                    <CheckCircle size={16} />
                    <span>Resolved by {alert.resolved_by_name || 'System'} on {new Date(alert.resolved_at).toLocaleString()}</span>
                    {alert.resolution_notes && <p className="resolution-notes">{alert.resolution_notes}</p>}
                  </div>
                )}
              </div>

              <div className="alert-actions">
                {!isResolved && (
                  <>
                    {(alert.alert_type === 'low_stock' || alert.alert_type === 'zero_stock') && (
                      <button
                        className="btn-sm btn-primary"
                        onClick={() => {
                          const notes = window.prompt('PO notes (optional):');
                          if (notes !== null) handleResolve(alert, notes, true);
                        }}
                        title="Create purchase order and resolve alert"
                      >
                        <CheckCircle size={16} /> Create PO + Resolve
                      </button>
                    )}

                    <button 
                      className="btn-sm btn-success"
                      onClick={() => {
                        const notes = window.prompt('Resolution notes (optional):');
                        if (notes !== null) handleResolve(alert, notes, false);
                      }}
                      title="Mark as resolved"
                    >
                      <CheckCircle size={16} /> Resolve
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {safeAlerts.length === 0 && (
        <div className="empty-state">
          <AlertTriangle size={48} />
          <h3>No Alerts</h3>
          <p>
            {filterStatus === 'pending' 
              ? 'All clear! No pending alerts at the moment.'
              : 'No alerts found for the selected filters.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default AlertsTab;
